import type { RouteLocationNormalized, Router, RouteRecordNormalized } from 'vue-router'
import { createRouter, createWebHashHistory, RouteRecordRaw } from 'vue-router'
import { isUrl } from '@/utils/is'
import { cloneDeep, omit } from 'lodash-es'
import qs from 'qs'

const modules = import.meta.glob('../views/**/*.{vue,tsx}')

/** 统一成 views 下的相对路径（去 ../views/、前导斜杠和后缀），避免子串误匹配。 */
const normalizeViewPath = (path: string) =>
  path
    .replace(/\\/g, '/')
    .replace(/^\.\.\/views\//, '')
    .replace(/^views\//, '')
    .replace(/^\/+/, '')
    .replace(/\.(vue|tsx)$/i, '')

const findModuleIndex = (path: string) => {
  const target = normalizeViewPath(path)
  const keys = Object.keys(modules)
  const exact = keys.findIndex((key) => normalizeViewPath(key) === target)
  if (exact !== -1) return exact
  return keys.findIndex((key) => normalizeViewPath(key) === `${target}/index`)
}

/**
 * 注册一个异步组件
 * @param componentPath 例:/bpm/oa/leave/detail
 */
export const registerComponent = (componentPath: string) => {
  const index = findModuleIndex(componentPath)
  if (index !== -1) {
    // 使用异步组件的方式来动态加载组件
    // @ts-ignore
    return defineAsyncComponent(modules[Object.keys(modules)[index]])
  }
}
/* Layout */
export const Layout = () => import('@/layout/Layout.vue')

export const getParentLayout = () => {
  return () =>
    new Promise((resolve) => {
      resolve({
        name: 'ParentLayout'
      })
    })
}

// 按照路由中meta下的rank等级升序来排序路由
export const ascending = (arr: any[]) => {
  arr.forEach((v) => {
    if (v?.meta?.rank === null) v.meta.rank = undefined
    if (v?.meta?.rank === 0) {
      if (v.name !== 'home' && v.path !== '/') {
        console.warn('rank only the home page can be 0')
      }
    }
  })
  return arr.sort((a: { meta: { rank: number } }, b: { meta: { rank: number } }) => {
    return a?.meta?.rank - b?.meta?.rank
  })
}

export const getRawRoute = (route: RouteLocationNormalized): RouteLocationNormalized => {
  if (!route) return route
  const { matched, ...opt } = route
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path
        }))
      : undefined) as RouteRecordNormalized[]
  }
}

/**
 * 路由名：优先用后端给的 componentName，其次用 path 推导。
 * 目录节点的 path 常为 null，推导结果是空串，而空名字会让「按名字找路由」的
 * flatMultiLevelRoutes 匹配到祖先记录，故再退一步用菜单名兜底。
 */
const getRouteName = (route: AppCustomRouteRecordRaw, routePath: string) =>
  (route.componentName && route.componentName.length > 0
    ? route.componentName
    : toCamelCase(routePath, true)) || toCamelCase(route.name, true)

// 后端控制路由生成
export const generateRoute = (routes: AppCustomRouteRecordRaw[]): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []
  const modulesRoutesKeys = Object.keys(modules)
  for (const route of routes) {
    // 容错：后端菜单 path 可能为 null（目录）或 component 指向前端不存在的组件
    const routePath = route.path || ''
    // 1. 生成 meta 菜单元数据
    const meta = {
      title: route.name,
      icon: route.icon,
      // 菜单显隐由后端 visible 字段决定：仅 visible === true 时显示，false / null 均隐藏
      hidden: route.visible !== true,
      noCache: !route.keepAlive,
      alwaysShow:
        route.children &&
        route.children.length === 1 &&
        (route.alwaysShow !== undefined ? route.alwaysShow : true)
    } as any
    // 特殊逻辑：如果后端配置的 MenuDO.component 包含 ?，则表示需要传递参数
    // 此时，我们需要解析参数，并且将参数放到 meta.query 中
    // 这样，后续在 Vue 文件中，可以通过 const { currentRoute } = useRouter() 中，通过 meta.query 获取到参数
    if (route.component && route.component.indexOf('?') > -1) {
      const query = route.component.split('?')[1]
      route.component = route.component.split('?')[0]
      meta.query = qs.parse(query)
    }

    // 2. 生成 data（AppRouteRecordRaw）
    // 路由地址转首字母大写驼峰，作为路由名称，适配keepAlive
    let data: AppRouteRecordRaw = {
      path: routePath.indexOf('?') > -1 ? routePath.split('?')[0] : routePath,
      name: getRouteName(route, routePath),
      redirect: route.redirect,
      meta: meta
    }
    //处理顶级非目录路由
    if (!route.children && route.parentId == 0 && route.component) {
      data.component = Layout
      // 外层是 Layout 容器，仅保留 hidden 用于菜单显隐（visible=false 的顶级菜单需被过滤）
      data.meta = { hidden: meta.hidden }
      data.name = toCamelCase(route.path, true) + 'Parent'
      data.redirect = ''
      meta.alwaysShow = true
      const childrenData: AppRouteRecordRaw = {
        path: '',
        name: getRouteName(route, routePath),
        redirect: route.redirect,
        meta: meta
      }
      const index = route?.component
        ? findModuleIndex(route.component)
        : findModuleIndex(route.path)
      if (index === -1) {
        continue
      }
      childrenData.component = modules[modulesRoutesKeys[index]]
      data.children = [childrenData]
    } else {
      // 目录
      if (route.children) {
        data.component = Layout
        data.redirect = getRedirect(routePath, route.children)
        // 外链
      } else if (isUrl(routePath)) {
        data = {
          path: '/external-link',
          component: Layout,
          meta: {
            name: route.name
          },
          children: [data]
        } as AppRouteRecordRaw
        // 菜单
      } else {
        // 对后端传component组件路径和不传做兼容（如果后端传component组件路径，那么path可以随便写，如果不传，component组件路径会根path保持一致）
        const index = route?.component
          ? findModuleIndex(route.component)
          : findModuleIndex(routePath)
        // 后端菜单指向的组件在前端不存在时，跳过该路由，由静态路由（remaining.ts）兜底
        if (index === -1) {
          continue
        }
        data.component = modules[modulesRoutesKeys[index]]
      }
      if (route.children) {
        data.children = generateRoute(route.children)
      }
    }
    res.push(data)
  }
  return res
}
export const getRedirect = (parentPath: string, children: AppCustomRouteRecordRaw[]) => {
  if (!children || children.length == 0) {
    return parentPath
  }
  const path = generateRoutePath(parentPath, children[0].path)
  // 递归子节点
  if (children[0].children) return getRedirect(path, children[0].children)
  return path
}
const generateRoutePath = (parentPath: string, path: string) => {
  if (parentPath.endsWith('/')) {
    parentPath = parentPath.slice(0, -1) // 移除默认的 /
  }
  // 目录节点的 path 可能为 null（后端 DIR 节点无路由），兜底避免 startsWith 报错
  if (!path) {
    return parentPath
  }
  if (!path.startsWith('/')) {
    path = '/' + path
  }
  return parentPath + path
}
export const pathResolve = (parentPath: string, path: string) => {
  if (isUrl(path)) return path
  // path 可能为 undefined（无子路由的叶子菜单），这里兜底避免 startsWith 报错
  const childPath = path ? (path.startsWith('/') ? path : `/${path}`) : ''
  return `${parentPath}${childPath}`.replace(/\/\//g, '/')
}

// 路由降级
export const flatMultiLevelRoutes = (routes: AppRouteRecordRaw[]) => {
  const modules: AppRouteRecordRaw[] = cloneDeep(routes)
  for (let index = 0; index < modules.length; index++) {
    const route = modules[index]
    if (!isMultipleRoute(route)) {
      continue
    }
    promoteRouteLevel(route)
  }
  return modules
}

// 层级是否大于2
const isMultipleRoute = (route: AppRouteRecordRaw) => {
  if (!route || !Reflect.has(route, 'children') || !route.children?.length) {
    return false
  }

  const children = route.children

  let flag = false
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    if (child.children?.length) {
      flag = true
      break
    }
  }
  return flag
}

// 生成二级路由
const promoteRouteLevel = (route: AppRouteRecordRaw) => {
  let router: Router | null = createRouter({
    routes: [route as RouteRecordRaw],
    history: createWebHashHistory()
  })

  const routes = router.getRoutes()
  addToChildren(routes, route.children || [], route)
  router = null

  route.children = route.children?.map((item) => omit(item, 'children'))
}

// 添加所有子菜单
const addToChildren = (
  routes: RouteRecordNormalized[],
  children: AppRouteRecordRaw[],
  routeModule: AppRouteRecordRaw
) => {
  routeModule.children = routeModule.children || []
  const target: AppRouteRecordRaw[] = routeModule.children
  // 用显式栈遍历并记录已下钻的节点，替代原先的递归：顶层调用里 children 与
  // routeModule.children 是同一个数组，边遍历边 push 会让遍历长度无界增长；
  // 一旦有同名节点（目录 path 为 null 时路由名会退化成同一个值），routes.find
  // 还会命中祖先记录并把它塞进自己的 children，递归下去就是栈溢出。
  const seen = new Set<AppRouteRecordRaw>()
  const stack: { list: AppRouteRecordRaw[]; index: number }[] = [{ list: [...children], index: 0 }]
  while (stack.length) {
    const frame = stack[stack.length - 1]
    if (frame.index >= frame.list.length) {
      stack.pop()
      continue
    }
    const child = frame.list[frame.index++]
    if (!child || seen.has(child)) {
      continue
    }
    seen.add(child)
    const route = routes.find((item) => item.name === child.name)
    if (!route) {
      continue
    }
    if (!target.some((item) => item.name === route.name)) {
      target.push(route as unknown as AppRouteRecordRaw)
    }
    if (child.children?.length) {
      stack.push({ list: [...child.children], index: 0 })
    }
  }
}
const toCamelCase = (str: string, upperCaseFirst: boolean) => {
  str = (str || '')
    .replace(/-(.)/g, function (group1: string) {
      return group1.toUpperCase()
    })
    .replaceAll('-', '')

  if (upperCaseFirst && str) {
    str = str.charAt(0).toUpperCase() + str.slice(1)
  }

  return str
}
